import React, { useState, useMemo } from 'react';
import { List, ListItem, ListItemText, Typography, Box, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { CompanyNote } from '../../../../../types/CompanyNote';
import { useIntl } from 'react-intl';

interface CompanyNotesListProps {
	notes: CompanyNote[];
	onNoteSelect: (note: CompanyNote) => void;
}

const CompanyNotesList: React.FC<CompanyNotesListProps> = ({ notes, onNoteSelect }) => {
	const { formatMessage } = useIntl();
	const [searchQuery, setSearchQuery] = useState('');

	const filteredNotes = useMemo(() => {
		if (!searchQuery.trim()) return notes;

		const query = searchQuery.toLowerCase();
		return notes.filter(
			note =>
				(note.title?.toLowerCase() || '').includes(query) ||
				(note.author?.toLowerCase() || '').includes(query) ||
				(note.content?.toLowerCase() || '').includes(query),
		);
	}, [notes, searchQuery]);

	if (notes.length === 0) {
		return (
			<Box sx={{ p: 2, textAlign: 'center' }}>
				<Typography color='text.secondary'>No notes available</Typography>
			</Box>
		);
	}

	return (
		<>
			<TextField
				variant='standard'
				placeholder={formatMessage({ id: 'companyNotes.searchPlaceholder' })}
				value={searchQuery}
				onChange={e => setSearchQuery(e.target.value)}
				sx={{ mb: 2, width: '100%' }}
				InputProps={{
					startAdornment: (
						<InputAdornment position='start'>
							<SearchIcon />
						</InputAdornment>
					),
				}}
			/>
			<List sx={{ width: '100%', bgcolor: 'background.paper' }}>
				{filteredNotes.map(note => (
					<ListItem
						key={note.companyNotesId}
						onClick={() => onNoteSelect(note)}
						sx={{
							cursor: 'pointer',
							'&:hover': {
								bgcolor: 'action.hover',
							},
							borderBottom: '1px solid',
							borderColor: 'divider',
						}}
					>
						<ListItemText
							primary={note.title}
							secondary={
								<React.Fragment>
									<Box sx={{ mb: 0.5 }}>
										<Typography component='span' variant='body2' color='text.primary'>
											{note.author}
										</Typography>
										{' — '}
										{note.timestamp}
									</Box>
									{note.content && (
										<Typography
											variant='body2'
											color='text.secondary'
											sx={{
												display: '-webkit-box',
												WebkitLineClamp: 2,
												WebkitBoxOrient: 'vertical',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
										>
											{note.content}
										</Typography>
									)}
								</React.Fragment>
							}
						/>
					</ListItem>
				))}
			</List>
		</>
	);
};

export default CompanyNotesList;
